/**
 * Pure facts for the team home dashboard (#2048, change 1).
 *
 * The home page answers one question first: is this week fine, and does
 * anything need me? Everything here is derived from reads the page already
 * makes (the team's solves list, the stitched roster for this week, the
 * workload overview's `home_summary`, the date requests) — no new endpoint.
 * Kept free of React so the wording and the edge cases are unit-tested on
 * their own (`web/tests/teamHomeFacts.test.ts`).
 */
import type { OnCallHorizonEntry } from "../../api/insightsDepthTypes";
import { parseISO } from "../../lib/dateWindow";
import {
  type DateWindow,
  addDaysIso,
  cycleWindow,
  daysOf,
  formatWindowSpan,
  intersectWindows,
  mondayOf,
} from "../../lib/rosterDatesWindow";
import { seatsShortOver } from "../../lib/seatsShort";
import { type SolveRowState, solveRowState } from "./SolvesList";
import type { Cycle } from "./types";

/** The slice of a solves-list row these facts read. */
export type HomeCycle = Pick<
  Cycle,
  | "id"
  | "horizon_start"
  | "horizon_days"
  | "state"
  | "phase"
  | "roster_held_as_drafts"
  | "summary"
  | "created_at"
>;

/** Monday to Sunday of the week holding `today`. */
export function weekOf(today: string): DateWindow {
  const from = mondayOf(today);
  return { from, to: addDaysIso(from, 6) };
}

/** "Week of 5 Oct – 11 Oct". */
export function weekLabel(week: DateWindow): string {
  return `Week of ${formatWindowSpan(week)}`;
}

function liveCycles<C extends HomeCycle>(cycles: readonly C[]): C[] {
  return cycles.filter((c) => c.phase !== "archived");
}

function isWeekend(iso: string): boolean {
  const dow = parseISO(iso).getDay();
  return dow === 0 || dow === 6;
}

/**
 * Whether the team rosters weekends at all: true when any live solve has
 * ever covered a Saturday or a Sunday. A team that only ever solves Monday
 * to Friday does not have "unsolved" weekends.
 */
export function teamRostersWeekends(cycles: readonly HomeCycle[]): boolean {
  return liveCycles(cycles).some((c) => daysOf(cycleWindow(c)).some(isWeekend));
}

/** The days of `w` the team rosters (weekends dropped for a weekday team). */
function rosteredDays(w: DateWindow, weekends: boolean): string[] {
  const days = daysOf(w);
  return weekends ? days : days.filter((d) => !isWeekend(d));
}

/** Days of `w` (that the team rosters) no live solve covers. */
export function unsolvedDays(w: DateWindow, cycles: readonly HomeCycle[]): string[] {
  const live = liveCycles(cycles);
  const weekends = teamRostersWeekends(cycles);
  return rosteredDays(w, weekends).filter(
    (d) =>
      !live.some((c) => {
        const cw = cycleWindow(c);
        return d >= cw.from && d <= cw.to;
      }),
  );
}

export type WeekState = "no_roster" | SolveRowState;

export interface WeekStatus {
  state: WeekState;
  /** Rostered days of the week no solve covers. */
  unsolvedDays: number;
  /** Empty seats on the week's published days (only when published). */
  seatsShort: number | null;
  /** The live solve holding today (else the week's first one). */
  cycleId: string | null;
}

/**
 * This week's state, from the solves that touch it. Published only when
 * every one is; "Solving" wins; otherwise solved-but-held beats not solved.
 * `shortfallsByDay` is the stitched roster's per-day empty seats.
 */
export function weekStatus(
  week: DateWindow,
  today: string,
  cycles: readonly HomeCycle[],
  shortfallsByDay: Record<string, number> | undefined,
): WeekStatus {
  const live = liveCycles(cycles);
  if (live.length === 0) {
    return { state: "no_roster", unsolvedDays: 0, seatsShort: null, cycleId: null };
  }
  const touching = live
    .filter((c) => intersectWindows(week, cycleWindow(c)) !== null)
    .sort((a, b) => a.horizon_start.localeCompare(b.horizon_start));
  const unsolved = unsolvedDays(week, cycles).length;
  const holdingToday = touching.find((c) => {
    const cw = cycleWindow(c);
    return today >= cw.from && today <= cw.to;
  });
  const cycleId = (holdingToday ?? touching[0])?.id ?? null;
  if (touching.length === 0) {
    return { state: "not_solved", unsolvedDays: unsolved, seatsShort: null, cycleId: null };
  }
  const states = touching.map((c) => solveRowState(c));
  let state: SolveRowState;
  if (states.includes("solving")) state = "solving";
  else if (states.every((s) => s === "published")) state = "published";
  else if (states.some((s) => s === "solved" || s === "published")) state = "solved";
  else state = "not_solved";
  let seatsShort: number | null = null;
  if (state === "published" && shortfallsByDay) {
    // #2027 item 5 — the one "short" definition, as the roster reads it.
    seatsShort = seatsShortOver(shortfallsByDay, daysOf(week)).seats;
  }
  return { state, unsolvedDays: unsolved, seatsShort, cycleId };
}

const STATE_WORDS: Record<SolveRowState, string> = {
  published: "Published",
  solved: "Solved, not published",
  not_solved: "Not solved",
  solving: "Solving",
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The status line's words after the week: "Published · covered". */
export function weekStatusWords(status: WeekStatus): string {
  if (status.state === "no_roster") return "No roster yet";
  return [STATE_WORDS[status.state], ...weekStatusDetails(status)].join(" · ");
}

/** The status line's words after the state: "covered", "2 seats short",
 *  "1 day not solved" (#2071 — the phone's Team home puts the state in a
 *  chip and these words under it). */
export function weekStatusDetails(status: WeekStatus): string[] {
  if (status.state === "no_roster") return [];
  const parts: string[] = [];
  if (status.state === "published" && status.seatsShort !== null) {
    parts.push(
      status.seatsShort === 0 ? "covered" : `${plural(status.seatsShort, "seat", "seats")} short`,
    );
  }
  if (status.state !== "not_solved" && status.unsolvedDays > 0) {
    parts.push(`${plural(status.unsolvedDays, "day", "days")} not solved`);
  }
  return parts;
}

/** The tone of the status line's dot. */
export function weekStatusTone(status: WeekStatus): "success" | "warning" | "neutral" {
  if (status.state === "published") {
    return status.seatsShort === 0 && status.unsolvedDays === 0 ? "success" : "warning";
  }
  if (status.state === "no_roster") return "neutral";
  return "warning";
}

// ── Needs you ──────────────────────────────────────────────────────────

/** Rostered days in the next `span` days (from today) no solve covers. */
export function upcomingUnsolved(today: string, cycles: readonly HomeCycle[], span = 14): string[] {
  if (liveCycles(cycles).length === 0) return [];
  return unsolvedDays({ from: today, to: addDaysIso(today, span - 1) }, cycles);
}

// ── Tiles ──────────────────────────────────────────────────────────────

export type OnCallHorizon =
  | { kind: "none" }
  | { kind: "open"; label: string; date: string; openTypes: number; total: number }
  | { kind: "covered"; total: number };

/** The on-call horizon in one fact: the first open call, else "covered".
 *  (`days_covered_ahead` counts runs per weekday-bound call type — a
 *  Monday-only call reads 0 on a Tuesday — so it is not summed up here.) */
export function onCallHorizon(entries: readonly OnCallHorizonEntry[]): OnCallHorizon {
  if (entries.length === 0) return { kind: "none" };
  const open = entries
    .filter((e) => e.next_uncovered_date != null)
    .sort((a, b) =>
      (a.next_uncovered_date as string).localeCompare(b.next_uncovered_date as string),
    );
  const first = open[0];
  if (first) {
    return {
      kind: "open",
      label: first.label,
      date: first.next_uncovered_date as string,
      openTypes: open.length,
      total: entries.length,
    };
  }
  return { kind: "covered", total: entries.length };
}

// ── Collapsed section summaries ────────────────────────────────────────

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function dayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()] ?? ""}`;
}

/** "4 solves · last published 21 Sep – 25 Sep" / "Nothing solved yet". */
export function solvesSummary(cycles: readonly HomeCycle[]): string {
  const live = liveCycles(cycles);
  if (live.length === 0) return "Nothing solved yet";
  const parts = [plural(live.length, "solve", "solves")];
  const published = live
    .filter((c) => solveRowState(c) === "published")
    .sort((a, b) => b.horizon_start.localeCompare(a.horizon_start))[0];
  if (published) {
    parts.push(`latest published ${formatWindowSpan(cycleWindow(published))}`);
  }
  const lastRun = live
    .map((c) => c.summary?.last_run_at ?? null)
    .filter((x): x is string => Boolean(x))
    .sort()
    .at(-1);
  if (lastRun) parts.push(`last solved ${dayMonth(lastRun)}`);
  return parts.join(" · ");
}

interface LeaveRow {
  request_type: string;
  start_date: string;
  end_date: string;
}

/** Leave days (leave + MC) that fall inside `month`, summed per person-day. */
export function leaveDaysIn(requests: readonly LeaveRow[], month: DateWindow): number {
  let n = 0;
  for (const r of requests) {
    if (r.request_type !== "leave" && r.request_type !== "mc") continue;
    const clip = intersectWindows(month, { from: r.start_date, to: r.end_date });
    if (clip) n += daysOf(clip).length;
  }
  return n;
}

/** The calendar month holding `today`. */
export function monthOf(today: string): DateWindow {
  const d = parseISO(today);
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  const to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return { from: iso(from), to: iso(to) };
}

/** "14 leave days this month · 2 to review". */
export function leaveSummary(leaveDays: number | null, toReview: number): string {
  const parts: string[] = [];
  if (leaveDays !== null) {
    parts.push(
      leaveDays === 0
        ? "No leave this month"
        : `${plural(leaveDays, "leave day", "leave days")} this month`,
    );
  }
  if (toReview > 0) parts.push(`${toReview} to review`);
  return parts.join(" · ");
}
